import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { RefundPreviewView } from '@/infrastructure/FulfillmentAdminClient';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import './cancelDialog.css';

/**
 * Refund only (SSD-7676): money back without cancelling the order or moving stock, for an order already on its way or
 * delivered. CS refunds this way only after the SOP's own answer (redelivery, reshipment, coupon) was refused and
 * Victoria approved the order, so the dialog asks who approved it and whether the customer is asked to send it back.
 */
export const REFUND_ONLY_REASONS = [
  'Customer insisted on a refund (approved exception)',
  'Lost or not delivered',
  'Damaged, wrong or missing item',
  'Late delivery',
  'Other',
] as const;
export type RefundOnlyReason = (typeof REFUND_ONLY_REASONS)[number];

export interface RefundOnlyForm {
  amount: string;
  reason: RefundOnlyReason;
  approvedBy: string;
  /** False: the customer keeps the goods. True: they are asked to send them back. */
  returnExpected: boolean;
  note: string;
}

export interface RefundOnlyAnswer {
  form: RefundOnlyForm;
  preview: RefundPreviewView;
}

interface RefundOnlyRequest {
  orderId: number;
  currency: string;
  /** The order total, offered as the amount; the server still caps it at what is left to refund. */
  defaultAmount: string;
  /** Checks the refund with the server; resolves to the preview or rejects with the sentence to show. */
  preview: (form: RefundOnlyForm) => Promise<RefundPreviewView>;
}

interface Pending extends RefundOnlyRequest {
  id: number;
  settle: (answer: RefundOnlyAnswer | null) => void;
}

/** The amount the server takes: positive, up to two decimals, no leading zeros. */
const AMOUNT = /^(?!0\d)(?!0+(?:\.0+)?$)\d+(?:\.\d{1,2})?$/;

/** The one line the order thread keeps for a refund only, so FC and CS read what was decided. */
export const describeRefundOnly = (form: RefundOnlyForm, currency: string): string =>
  [
    `Refund only: ${currency} ${form.amount}`,
    form.reason,
    `approved by ${form.approvedBy.trim()}`,
    form.returnExpected ? 'customer asked to send it back' : 'no return asked, the customer keeps it',
    form.note.trim(),
  ]
    .filter((part) => part !== '')
    .join(' · ');

const RefundOnlyModal = ({ orderId, currency, defaultAmount, preview, settle }: Pending) => {
  const [amount, setAmount] = useState(defaultAmount);
  const [reason, setReason] = useState<RefundOnlyReason | ''>('');
  const [approvedBy, setApprovedBy] = useState('Victoria');
  const [returnExpected, setReturnExpected] = useState<boolean | null>(null);
  const [note, setNote] = useState('');
  const [checked, setChecked] = useState<RefundPreviewView | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useModalDialog(() => settle(null));
  const amountId = useId();
  const reasonId = useId();
  const approverId = useId();
  const noteId = useId();
  const noteRequired = reason === 'Other';
  const holdingWhy = !AMOUNT.test(amount.trim())
    ? 'Write the amount, like 20 or 20.50'
    : reason === ''
      ? 'Pick a reason'
      : approvedBy.trim() === ''
        ? 'Write who approved it'
        : returnExpected === null
          ? 'Say whether the customer sends it back'
          : noteRequired && note.trim() === ''
            ? 'Write what the reason was'
            : null;
  const form = (): RefundOnlyForm => ({
    amount: amount.trim(),
    reason: reason as RefundOnlyReason,
    approvedBy: approvedBy.trim(),
    returnExpected: returnExpected === true,
    note: note.trim(),
  });
  const check = async () => {
    if (holdingWhy !== null || checking) {
      return;
    }
    setChecking(true);
    setError('');
    try {
      setChecked(await preview(form()));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The refund could not be checked.');
    } finally {
      setChecking(false);
    }
  };

  return (
    <dialog className="wms-prompt cancel-prompt" ref={dialogRef} aria-label="Refund only">
      <header>
        Refund only
        <button type="button" className="x" aria-label="Close" onClick={() => settle(null)}>
          ✕
        </button>
      </header>
      <div className="body">
        <div className="cancel-chips">
          <span className="cancel-chip is-amber" data-testid="refund-only-notice">
            Money back only: the order is not cancelled and stock does not move
          </span>
        </div>
        {checked === null ? (
          <>
            <div className="refund-row">
              <div className="cancel-note">
                <div className="cancel-note-head">
                  <label htmlFor={amountId}>
                    <b>Amount ({currency})</b>
                  </label>
                </div>
                <input
                  id={amountId}
                  className="refund-input"
                  inputMode="decimal"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                />
              </div>
              <div className="cancel-note">
                <div className="cancel-note-head">
                  <label htmlFor={approverId}>
                    <b>Approved by</b>
                  </label>
                </div>
                <input
                  id={approverId}
                  className="refund-input"
                  value={approvedBy}
                  onChange={(event) => setApprovedBy(event.target.value)}
                />
              </div>
            </div>
            <div className="qrow cancel-reason">
              <label htmlFor={reasonId}>
                <b>Reason</b>
              </label>
              <select
                id={reasonId}
                value={reason}
                onChange={(event) => setReason(event.target.value as RefundOnlyReason | '')}
              >
                <option value="">Select a reason</option>
                {REFUND_ONLY_REASONS.map((choice) => (
                  <option key={choice} value={choice}>
                    {choice}
                  </option>
                ))}
              </select>
            </div>
            <fieldset className="cancel-goods">
              <legend>The goods</legend>
              <span className="cancel-goods-choice refund-goods" role="radiogroup" aria-label="The goods">
                {([false, true] as const).map((value) => (
                  <label key={String(value)} className={returnExpected === value ? 'is-picked' : undefined}>
                    <input
                      type="radio"
                      name={`refund-goods-${orderId}`}
                      checked={returnExpected === value}
                      onChange={() => setReturnExpected(value)}
                    />
                    {value ? 'Customer sends it back' : 'Customer keeps it (no return)'}
                  </label>
                ))}
              </span>
            </fieldset>
            <div className="cancel-note">
              <div className="cancel-note-head">
                <label htmlFor={noteId}>
                  <b>Note</b>
                </label>
                <span className={`cancel-note-tag${noteRequired ? ' is-required' : ''}`}>
                  {noteRequired ? 'Required' : 'Optional'}
                </span>
              </div>
              <textarea
                id={noteId}
                aria-label="Note"
                rows={2}
                maxLength={200}
                value={note}
                placeholder="Ticket number or what was agreed with the customer"
                onChange={(event) => setNote(event.target.value)}
              />
              <p className="cancel-note-help">Goes to the order comments with the refund.</p>
            </div>
          </>
        ) : (
          <div className="refund-confirm" data-testid="refund-only-preview">
            <p className="refund-confirm-amount">
              Refund <b>{`${checked.preview.currency} ${checked.preview.amount}`}</b>
            </p>
            <p>
              Left to refund: {checked.preview.refundableBefore} → {checked.preview.refundableAfter}
            </p>
            <p>{describeRefundOnly(form(), currency)}</p>
          </div>
        )}
        {error && (
          <p className="refund-error" role="alert">
            {error}
          </p>
        )}
      </div>
      <div className="foot cancel-foot">
        <div className="cancel-actions">
          <span className="cancel-holding" data-testid="refund-holding">
            {checked === null ? (holdingWhy ?? '') : 'Nothing has been refunded yet'}
          </span>
          {checked === null ? (
            <>
              <button type="button" className="btn btn-line btn-sm" onClick={() => settle(null)}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-line btn-sm"
                disabled={holdingWhy !== null || checking}
                onClick={() => void check()}
              >
                {checking ? 'Checking…' : 'Check refund'}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-line btn-sm" onClick={() => setChecked(null)}>
                Back
              </button>
              <button
                type="button"
                className="btn btn-red-line btn-sm"
                onClick={() => settle({ form: form(), preview: checked })}
              >
                {`Refund ${checked.preview.currency} ${checked.preview.amount}`}
              </button>
            </>
          )}
        </div>
      </div>
    </dialog>
  );
};

/** Opens the refund-only dialog and resolves to what was checked and confirmed, or null when it is closed. */
export const useRefundOnlyDialog = () => {
  const [pending, setPending] = useState<Pending | null>(null);
  const nextId = useRef(0);
  const resolvePending = useRef<((answer: RefundOnlyAnswer | null) => void) | null>(null);
  useEffect(
    () => () => {
      resolvePending.current?.(null);
    },
    [],
  );
  const askRefundOnly = (request: RefundOnlyRequest) =>
    new Promise<RefundOnlyAnswer | null>((resolve) => {
      resolvePending.current?.(null);
      resolvePending.current = resolve;
      nextId.current += 1;
      setPending({
        ...request,
        id: nextId.current,
        settle: (answer) => {
          if (resolvePending.current !== resolve) {
            return;
          }
          resolvePending.current = null;
          setPending(null);
          resolve(answer);
        },
      });
    });
  /** Closes an open question as backed out, for a page that can no longer stand behind what it asked about. */
  const dismiss = useCallback(() => {
    resolvePending.current?.(null);
    resolvePending.current = null;
    setPending(null);
  }, []);
  return { askRefundOnly, dismiss, dialog: pending ? <RefundOnlyModal key={pending.id} {...pending} /> : null };
};
